/**
 * SayItBetter Analytics — unified helper that auto-detects native (Capacitor) vs web
 * and routes events to the correct Firebase SDK.
 *
 * Web uses `firebase/analytics` (JS SDK). Native Android uses `@capacitor-firebase/analytics`.
 *
 * Config is read from REACT_APP_FIREBASE_* env vars. If `REACT_APP_FIREBASE_API_KEY` is
 * not set, analytics is a no-op (so dev/preview don't spam events).
 */

import { initializeApp, getApps } from "firebase/app";
import { getAnalytics, logEvent as webLogEvent, setUserId as webSetUserId, setUserProperties as webSetUserProperties, isSupported } from "firebase/analytics";

let _nativeAnalytics = null;
let _webAnalytics = null;
let _isNative = false;
let _initialized = false;
let _enabled = false;

function hasConfig() {
  return !!process.env.REACT_APP_FIREBASE_API_KEY;
}

async function detectNative() {
  try {
    // Lazy import so web builds without Capacitor don't fail
    const { Capacitor } = await import("@capacitor/core");
    return Capacitor.isNativePlatform && Capacitor.isNativePlatform();
  } catch {
    return false;
  }
}

async function initWeb() {
  if (_webAnalytics) return _webAnalytics;
  const firebaseConfig = {
    apiKey: process.env.REACT_APP_FIREBASE_API_KEY,
    authDomain: process.env.REACT_APP_FIREBASE_AUTH_DOMAIN,
    projectId: process.env.REACT_APP_FIREBASE_PROJECT_ID,
    storageBucket: process.env.REACT_APP_FIREBASE_STORAGE_BUCKET,
    messagingSenderId: process.env.REACT_APP_FIREBASE_MESSAGING_SENDER_ID,
    appId: process.env.REACT_APP_FIREBASE_APP_ID,
    measurementId: process.env.REACT_APP_FIREBASE_MEASUREMENT_ID,
  };
  const app = getApps()[0] || initializeApp(firebaseConfig);
  const supported = await isSupported().catch(() => false);
  if (!supported) return null;
  _webAnalytics = getAnalytics(app);
  return _webAnalytics;
}

async function initNative() {
  if (_nativeAnalytics) return _nativeAnalytics;
  try {
    const { FirebaseAnalytics } = await import("@capacitor-firebase/analytics");
    _nativeAnalytics = FirebaseAnalytics;
    await FirebaseAnalytics.setEnabled({ enabled: true });
    return FirebaseAnalytics;
  } catch (e) {
    return null;
  }
}

export async function initAnalytics() {
  if (_initialized) return;
  _initialized = true;
  if (!hasConfig()) {
    if (process.env.NODE_ENV === "development") {
      console.info("[analytics] Firebase not configured — analytics disabled.");
    }
    return;
  }
  _isNative = await detectNative();
  if (_isNative) {
    await initNative();
  } else {
    await initWeb();
  }
  _enabled = true;
}

/**
 * Log a custom event.
 * @param {string} name - snake_case event name
 * @param {Object} [params] - optional event params
 */
export async function trackEvent(name, params = {}) {
  if (!_enabled) return;
  try {
    if (_isNative && _nativeAnalytics) {
      await _nativeAnalytics.logEvent({ name, params });
    } else if (_webAnalytics) {
      webLogEvent(_webAnalytics, name, params);
    }
  } catch (e) {
    // Swallow — analytics must never break the app
    if (process.env.NODE_ENV === "development") {
      console.warn("[analytics] trackEvent failed", name, e);
    }
  }
}

export async function setAnalyticsUser(userId, userProperties = {}) {
  if (!_enabled) return;
  try {
    if (_isNative && _nativeAnalytics) {
      await _nativeAnalytics.setUserId({ userId: userId || null });
      for (const [key, value] of Object.entries(userProperties)) {
        await _nativeAnalytics.setUserProperty({ key, value: String(value) });
      }
    } else if (_webAnalytics) {
      webSetUserId(_webAnalytics, userId);
      if (Object.keys(userProperties).length) {
        webSetUserProperties(_webAnalytics, userProperties);
      }
    }
  } catch (e) {
    // swallow
  }
}

export async function trackPageView(pagePath, pageTitle) {
  return trackEvent("page_view", {
    page_path: pagePath,
    page_title: pageTitle || document.title,
  });
}

// Convenience wrappers for the standard event set
export const Events = {
  login: (method = "password") => trackEvent("login", { method }),
  signUp: (method = "password") => trackEvent("sign_up", { method }),
  logout: () => trackEvent("logout"),
  analyzeMessage: (risk_level, relationship_type) =>
    trackEvent("analyze_message", { risk_level, relationship_type }),
  viewPricing: (source) => trackEvent("view_pricing", { source: source || "unknown" }),
  upgradeClick: (plan, source) =>
    trackEvent("upgrade_click", { plan, source: source || "unknown" }),
  shareApp: (method) => trackEvent("share_app", { method: method || "unknown" }),
  completeOnboarding: () => trackEvent("complete_onboarding"),
};
